// src/hooks/useSession.js
import { useState, useCallback, useRef, useEffect } from 'react';
import { fetchMessagesWithToken, persistMessageWithToken } from '../api/ai';

const STORAGE_KEY = 'ai_session_id';

export function useSession(getToken) {
  const getInitialSessionId = () => {
    const newId = crypto.randomUUID ? crypto.randomUUID() : `session-${Date.now()}`;
    sessionStorage.setItem(STORAGE_KEY, newId);
    localStorage.setItem(STORAGE_KEY, newId);
    return newId;
  };

  const [sessionId, setSessionIdState] = useState(getInitialSessionId);
  const [sessionFull, setSessionFull] = useState(false);

  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const updateSessionId = useCallback((newId) => {
    sessionStorage.setItem(STORAGE_KEY, newId);
    localStorage.setItem(STORAGE_KEY, newId);
    sessionIdRef.current = newId;
    setSessionIdState(newId);
  }, []);

  const startNewSession = useCallback(() => {
    const newId = crypto.randomUUID ? crypto.randomUUID() : `session-${Date.now()}`;
    updateSessionId(newId);
    setSessionFull(false);
    return newId;
  }, [updateSessionId]);

  const loadSessionMessages = useCallback(async (sid) => {
    const token = getToken();
    if (!token) return [];
    try {
      const data = await fetchMessagesWithToken(token, sid);
      return (Array.isArray(data) ? data : []).map(msg => ({
        id: msg.id, role: msg.role, content: msg.content,
        thinkingSteps: [], cards: null,
      }));
    } catch {
      return [];
    }
  }, [getToken]);

  const persistMessage = useCallback(async (role, content) => {
    const token = getToken();
    if (!token) return;
    const currentId = sessionIdRef.current;
    try {
      await persistMessageWithToken(token, { session_id: currentId, role, content });
    } catch (e) {
      throw e;
    }
  }, [getToken]);

  return {
    sessionId,
    setSessionId: updateSessionId,
    sessionFull,
    setSessionFull,
    startNewSession,
    loadSessionMessages,
    persistMessage,
  };
}